import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, CircleAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { AssistantTeamRun } from '../../../../common/hooks/use-assistant-api.hook';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsAgentRun } from '../../../../common/models/content-studio.model';
import { LiveMember, LiveStep, LiveTurn, TeamReport, stepCount } from '../../assistant/live-turn';
import { agentName, elapsed, toolLabel } from '../agents';
import { AgentAvatar } from './AgentAvatar';

/**
 * The work, while it happens.
 *
 * What makes an agent feel like one is seeing it act: the lead reading, then
 * handing parts of the question to specialists who each go off and read their
 * own corner at the same time, and come back with what they found. This draws
 * exactly that from the live turn — nothing here is decoration; every row is a
 * tool call the server made.
 *
 * `compact` is the side panel's width: members stack instead of sitting in a
 * grid, and each shows only its latest step.
 */

const StepIcon: React.FC<{ status: LiveStep['status'] }> = ({ status }) =>
    status === 'running' ? (
        <Spinner className="size-3.5 text-muted-foreground" />
    ) : status === 'failed' ? (
        <X className="size-3.5 text-destructive" aria-hidden />
    ) : (
        <Check className="size-3.5 text-muted-foreground" aria-hidden />
    );

const StepRow: React.FC<{ step: LiveStep }> = ({ step }) => {
    const { t } = useTranslation();
    return (
        <li className={cn('flex items-center gap-2 text-xs', step.status === 'running' ? 'text-foreground' : 'text-muted-foreground')}>
            <StepIcon status={step.status} />
            <span className="truncate">{toolLabel(t, step.tool)}</span>
        </li>
    );
};

/** A dot for the tone of a finding — the one place colour says something. */
const TONE_DOT: Record<string, string> = {
    good: 'bg-success',
    risk: 'bg-destructive',
    info: 'bg-muted-foreground/50',
};

export const ReportView: React.FC<{ report: TeamReport; compact?: boolean }> = ({ report, compact }) => (
    <div className="flex flex-col gap-2">
        <p className="text-sm leading-snug text-foreground">{report.headline}</p>
        {!!report.findings?.length && (
            <ul className="flex flex-col gap-1.5">
                {report.findings.slice(0, compact ? 2 : 4).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                        <span className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', TONE_DOT[f.tone] ?? TONE_DOT.info)} aria-hidden />
                        <span className="min-w-0 flex-1">
                            <span className="font-medium text-foreground">{f.title}</span>
                            {!compact && <span className="text-muted-foreground"> — {f.detail}</span>}
                        </span>
                        {f.figure && <span className="shrink-0 font-medium tabular-nums text-foreground">{f.figure}</span>}
                    </li>
                ))}
            </ul>
        )}
    </div>
);

const MemberCard: React.FC<{ member: LiveMember; compact?: boolean }> = ({ member, compact }) => {
    const { t } = useTranslation();
    const working = member.status === 'working';
    const steps = compact ? member.steps.slice(-1) : member.steps.slice(-4);

    return (
        <div
            className={cn(
                'flex min-w-0 flex-col gap-2.5 rounded-xl border bg-background p-3 transition-colors',
                working ? 'border-foreground/25' : 'border-border',
            )}
        >
            <div className="flex items-start gap-2.5">
                <AgentAvatar agent={member.agent} size="sm" working={working} />
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{agentName(t, member.agent, member.name)}</p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{member.brief}</p>
                </div>
                {member.status === 'failed' && <CircleAlert className="size-4 shrink-0 text-destructive" aria-hidden />}
            </div>

            {working && (
                <ul className="flex flex-col gap-1">
                    {steps.length ? steps.map((s, i) => <StepRow key={i} step={s} />) : (
                        <li className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Spinner className="size-3.5" />
                            {t('AGENTS.LIVE.STARTING', 'Getting started')}
                        </li>
                    )}
                </ul>
            )}

            {member.status === 'done' && member.report && <ReportView report={member.report} compact={compact} />}
            {member.status === 'failed' && (
                <p className="text-xs text-muted-foreground">{member.error || t('AGENTS.LIVE.FAILED', 'Could not finish.')}</p>
            )}
        </div>
    );
};

/** Ticks once a second while mounted, for the elapsed clock. */
function useNow(active: boolean): number {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!active) return;
        const id = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [active]);
    return now;
}

export const LiveWork: React.FC<{ turn: LiveTurn; compact?: boolean }> = ({ turn, compact }) => {
    const { t } = useTranslation();
    const now = useNow(true);
    const [open, setOpen] = useState(true);
    const steps = compact ? turn.steps.slice(-2) : turn.steps.slice(-5);
    const working = turn.team.filter((m) => m.status === 'working').length;

    return (
        <div className="flex flex-col gap-3" aria-live="polite">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 self-start text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
                <Spinner className="size-3.5" />
                <span className="font-medium text-foreground">
                    {working
                        ? t('AGENTS.LIVE.TEAM_WORKING', { count: working, defaultValue: '{{count}} agents working' })
                        : t('AGENTS.LIVE.WORKING', 'Working')}
                </span>
                <span>·</span>
                <span className="tabular-nums">{elapsed(now - turn.startedAt)}</span>
                {stepCount(turn) > 0 && (
                    <>
                        <span>·</span>
                        <span>{t('AGENTS.LIVE.STEPS', { count: stepCount(turn), defaultValue: '{{count}} steps' })}</span>
                    </>
                )}
                <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} aria-hidden />
            </button>

            {open && (
                <>
                    {!!steps.length && (
                        <ul className="flex flex-col gap-1 border-s ps-3">
                            {steps.map((s, i) => <StepRow key={i} step={s} />)}
                        </ul>
                    )}

                    {!!turn.team.length && (
                        <div className={cn('grid items-start gap-2', compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2')}>
                            {turn.team.map((m) => <MemberCard key={m.runId} member={m} compact={compact} />)}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/**
 * Who worked on a finished reply, collapsed to one row.
 *
 * Opened, each member shows the line it came back with; asked for more, the
 * member's own run is fetched and its steps and findings replayed — from the
 * run, not from anything the browser kept, so a reload shows what really
 * happened.
 */
export const TeamRecap: React.FC<{ team: AssistantTeamRun[]; compact?: boolean }> = ({ team, compact }) => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const [open, setOpen] = useState(false);
    const [runs, setRuns] = useState<Record<string, CsAgentRun | null>>({});

    const load = async (runId: string) => {
        if (runId in runs) return;
        setRuns((prev) => ({ ...prev, [runId]: null }));
        try {
            const run = await api.getAgentRun(runId);
            setRuns((prev) => ({ ...prev, [runId]: run }));
        } catch {
            // The headline is already on screen; the detail is a bonus.
        }
    };

    return (
        <div className="mb-2.5 flex flex-col gap-2 whitespace-normal">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 self-start text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
                <span className="flex -space-x-1.5 rtl:space-x-reverse">
                    {team.slice(0, 4).map((m) => <AgentAvatar key={m.runId} agent={m.agent} size="sm" />)}
                </span>
                {t('AGENTS.RECAP.WORKED_WITH', {
                    names: team.map((m) => agentName(t, m.agent, m.name)).join(', '),
                    defaultValue: 'Worked with {{names}}',
                })}
                <ChevronDown className={cn('size-3.5 transition-transform', !open && '-rotate-90')} aria-hidden />
            </button>

            {open && (
                <div className={cn('grid items-start gap-2', compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2')}>
                    {team.map((m) => {
                        const run = runs[m.runId];
                        const report = run?.result?.report as TeamReport | undefined;
                        return (
                            <div key={m.runId} className="flex flex-col gap-2 rounded-xl border bg-background p-3">
                                <div className="flex items-center gap-2.5">
                                    <AgentAvatar agent={m.agent} size="sm" />
                                    <p className="min-w-0 flex-1 truncate text-sm font-medium">{agentName(t, m.agent, m.name)}</p>
                                    {m.status === 'failed' && <CircleAlert className="size-4 text-destructive" aria-hidden />}
                                </div>
                                {report ? (
                                    <>
                                        <ReportView report={report} compact={compact} />
                                        {!!run?.steps?.length && (
                                            <ul className="flex flex-col gap-1 border-s ps-3">
                                                {run.steps.map((s) => (
                                                    <StepRow key={s.index} step={{ tool: s.tool, status: s.status === 'failed' ? 'failed' : 'done' }} />
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {m.headline && <p className="text-sm leading-snug">{m.headline}</p>}
                                        <button
                                            type="button"
                                            onClick={() => void load(m.runId)}
                                            className="self-start text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                                        >
                                            {run === null
                                                ? t('AGENTS.RECAP.LOADING', 'Loading…')
                                                : t('AGENTS.RECAP.SHOW_WORK', 'Show the work')}
                                        </button>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
